import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';

export default function ColorizePage() {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(null);
  const [strength, setStrength] = useState(80);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    user,
    pointsCost,
    controls,
  } = useToolGenerate('colorize');

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const canGenerate = !!image;

  const handleGenerate = () => {
    if (!canGenerate) return;
    doGenerate('', image ?? undefined, undefined, { colorizeStrength: strength });
  };

  return (
    <ToolPageLayout
      title={t('extool_colorize')}
      icon="ri-palette-fill"
      iconBg="linear-gradient(135deg, #EC4899, #F472B6)"
      iconColor="#EC4899"
      description={t('extool_colorize_desc')}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={canGenerate}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      {/* Upload image */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #EC4899, #F472B6)' }}
          >
            <i className="ri-image-add-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('colorize_uploadLabel')}</span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {image ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-3"
            style={{ aspectRatio: '16/9', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={image} alt="upload" className="w-full h-full object-contain" />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => pickPhotoFromLibrary(fileInputRef, setImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '16/9',
              background: 'rgba(236,72,153,0.08)',
              border: '2px dashed rgba(236,72,153,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(236,72,153,0.12)' }}
            >
              <i className="ri-upload-cloud-2-line text-2xl text-pink-400" />
            </div>
            <p className="text-sm text-gray-500">{t('colorize_uploadPlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      {/* Strength slider */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #EC4899, #F472B6)' }}
          >
            <i className="ri-hd-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('colorize_strengthLabel')}</span>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400">{t('colorize_gentle')}</span>
          <input
            type="range"
            min="0"
            max="100"
            value={strength}
            onChange={(e) => setStrength(Number(e.target.value))}
            className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #EC4899 0%, #F472B6 ${strength}%, #E5E7EB ${strength}%, #E5E7EB 100%)`,
            }}
          />
          <span className="text-xs text-gray-400">{t('colorize_strong')}</span>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          {t('colorize_currentStrength')}: <span className="font-semibold text-pink-500">{strength}%</span>
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
